import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Edit, Trash2, Package, ArrowLeft } from 'lucide-react';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InventoryItemsPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', sku: '', category_id: '', base_uom_id: '',
    reorder_point: 0, par_level: 0, notes: ''
  });

  const { token = localStorage.getItem('backoffice_token') } = useOutletContext() || {};
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes, uomsRes] = await Promise.all([
        fetch(`${API_URL}/api/inv/items?limit=200`, { headers }),
        fetch(`${API_URL}/api/inv/categories`, { headers }),
        fetch(`${API_URL}/api/inv/uom`, { headers })
      ]);
      
      if (itemsRes.ok) setItems(await itemsRes.json());
      if (catsRes.ok) setCategories(await catsRes.json());
      if (uomsRes.ok) setUoms(await uomsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem 
        ? `${API_URL}/api/inv/items/${editingItem.id}`
        : `${API_URL}/api/inv/items`;
      
      const method = editingItem ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          ...formData,
          reorder_point: parseFloat(formData.reorder_point) || 0,
          par_level: parseFloat(formData.par_level) || 0
        })
      });
      
      if (res.ok) {
        setShowDialog(false);
        setEditingItem(null);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error saving item');
      }
    } catch (err) {
      console.error('Error saving item:', err);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      sku: item.sku || '',
      category_id: item.category_id || '',
      base_uom_id: item.base_uom_id || '',
      reorder_point: item.reorder_point || 0,
      par_level: item.par_level || 0,
      notes: item.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/inv/items/${item.id}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) fetchData();
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', sku: '', category_id: '', base_uom_id: '',
      reorder_point: 0, par_level: 0, notes: ''
    });
  };

  const filteredItems = items.filter(item => {
    const matchSearch = item.name.toLowerCase().includes(search.toLowerCase()) ||
      (item.sku && item.sku.toLowerCase().includes(search.toLowerCase()));
    const matchCategory = categoryFilter === 'all' || item.category_id === categoryFilter;
    return matchSearch && matchCategory;
  });

  return (
    <div className="p-6 space-y-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="inventory-items-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href={bo('inventory')} className="p-2 hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Inventory Items</h1>
          <p className="text-gray-400">Manage your inventory items and stock levels</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditingItem(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-gray-800 border-gray-700"
                    placeholder="Item name"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">SKU</Label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="SKU-001"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Category</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Unit of Measure</Label>
                  <Select
                    value={formData.base_uom_id}
                    onValueChange={(v) => setFormData({ ...formData, base_uom_id: v })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700">
                      <SelectValue placeholder="Select UOM" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {uoms.map(uom => (
                        <SelectItem key={uom.id} value={uom.id}>{uom.code} - {uom.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Reorder Point</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.reorder_point}
                    onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Par Level</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.par_level}
                    onChange={(e) => setFormData({ ...formData, par_level: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-item-btn">
                  {editingItem ? 'Update' : 'Create'} Item
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-gray-800 border-gray-700"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 bg-gray-800 border-gray-700">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Items Table */}
      <Card className="bg-gray-900/50 border-gray-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left p-4 text-gray-400 font-medium">Item</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Category</th>
                  <th className="text-left p-4 text-gray-400 font-medium">UOM</th>
                  <th className="text-right p-4 text-gray-400 font-medium">On Hand</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Reorder Pt</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Avg Cost</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => (
                  <tr key={item.id} className="border-t border-gray-700 hover:bg-gray-800/30">
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{item.name}</p>
                        <p className="text-sm text-gray-400">{item.sku || 'No SKU'}</p>
                      </div>
                    </td>
                    <td className="p-4 text-gray-400">{item.category_name || '-'}</td>
                    <td className="p-4 text-gray-400">{item.base_uom_code || '-'}</td>
                    <td className="p-4 text-right text-white font-medium">
                      {item.on_hand?.toFixed(2)}
                    </td>
                    <td className="p-4 text-right text-gray-400">
                      {item.reorder_point?.toFixed(2)}
                    </td>
                    <td className="p-4 text-right text-gray-400">
                      ${item.avg_unit_cost?.toFixed(4)}
                    </td>
                    <td className="p-4 text-center">
                      {item.on_hand <= item.reorder_point ? (
                        <Badge variant="destructive">Low</Badge>
                      ) : item.enabled ? (
                        <Badge className="bg-green-600">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-item-${item.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(item)}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`delete-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-gray-400">
                      {loading ? 'Loading...' : 'No items found. Add your first item!'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
